import type { ApiError } from './errors'

/** Frontend §3.2. Each app calls `configureApi()` once at startup. */
export interface ApiConfig {
  /** `VITE_API_ORIGIN`, for example `http://localhost:8080`. */
  baseUrl: string
  /** Patient: `'include'` (cookies). Staff: `'omit'`. */
  credentials: RequestCredentials
  /** Staff only (Firebase). `forceRefresh` is passed once after a 401. */
  getIdToken?: (forceRefresh?: boolean) => Promise<string | null>
  /** Clinic app only: the impersonation id for `/v1/me` and the impersonated clinic's paths, else null. */
  getImpersonationId?: (path: string) => string | null
  /** Staff: `401 unauthorized` after one forced refresh and one replay → A-06. */
  onAuthFailure?: (e: ApiError) => void
  /** Staff: `password_change_required` | `impersonation_ended` | `clinic_inactive` | `no_access` → guard redirect. */
  onAccessError?: (e: ApiError) => void
  /** Reads 10 000; callers override per call (lookup 20 000, booking 30 000). */
  defaultTimeoutMs: number
  /**
   * Validate 2xx bodies with the operation's generated zod response schema (step 9). Apps pass
   * `VITE_ENV !== 'prod'`: production never rejects a response (API.md §1.10).
   */
  validateResponses: boolean
}

let current: ApiConfig | undefined

export function configureApi(config: ApiConfig): void {
  current = config
}

export function getApiConfig(): ApiConfig {
  if (!current) throw new Error('configureApi() has not been called')
  return current
}

/** Tests only: forget the configuration. */
export function resetApiConfig(): void {
  current = undefined
}
